/**
 * Cart Drawer v3 — JavaScript Controller (HeyShape-inspired)
 * ===========================================================
 * Handles: open/close, AJAX cart, multi-tier progress bar, quantity, upsells,
 * mini variant popup, free gift tiers, promo code, savings display, ATC interception.
 */
(function () {
  'use strict';

  const CFG = window.cartDrawerConfig || {};
  const SELECTORS = {
    drawer: '#cart-drawer',
    overlay: '#cart-drawer-overlay',
    close: '#cart-drawer-close',
    body: '#cart-drawer-body',
    items: '#cd-items',
    empty: '#cd-empty-state',
    subtotal: '#cd-subtotal-amount',
    headerCount: '#cd-header-count',
    // Total line
    totalCompare: '#cd-total-compare',
    totalSavings: '#cd-total-savings',
    // Promo code
    promoInput: '#cd-promo-input',
    promoApply: '#cd-promo-apply',
    // Upsells
    upsells: '#cd-upsells',
    upsellGrid: '#cd-upsells-grid',
    upsellPrev: '#cd-upsell-prev',
    upsellNext: '#cd-upsell-next',
    // Checkout
    checkout: '#cd-checkout-btn',
    // Templates
    itemTemplate: '#cd-item-template',
    // Variant popup
    variantPopup: '#cd-variant-popup',
    popupImg: '#cd-popup-img',
    popupName: '#cd-popup-name',
    popupPrice: '#cd-popup-price',
    popupOptions: '#cd-popup-options',
    popupAtc: '#cd-popup-atc',
    popupClose: '#cd-popup-close',
  };

  let cart = null;
  let isUpdating = false;
  let popupProduct = null;
  let popupSelectedOptions = [];

  // ---- Helpers ----

  function qs(sel) { return document.querySelector(sel); }
  function qsa(sel) { return document.querySelectorAll(sel); }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const fmt = CFG.moneyFormat || '${{amount}}';
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  async function fetchJSON(url, opts = {}) {
    const res = await fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...opts.headers },
    });
    if (!res.ok) throw new Error(`Cart API error: ${res.status}`);
    return res.json();
  }

  // ---- Cart API ----

  async function getCart() {
    const startedAt = performance.now();
    cart = await fetchJSON('/cart.js');
    await syncBundleCode();
    await syncSashaCode();
    // a full fetch (codes synced) that began after the add landed: the total is final
    if (pend.on && pend.addDoneAt && startedAt >= pend.addDoneAt) pend.synced = true;
    return cart;
  }

  // ---- Open the drawer the moment any add-to-cart starts (2026-10-17) ----
  // Product pages add, apply their codes, then call CartDrawer.open(). On the Sasha 2-pack
  // that left the page frozen on "Adding to cart" for ~1.6 s. Every add from any page now
  // slides the drawer in at the tap with a loading line, until a full fetch (codes synced)
  // that started after the add renders, so a pre-code price is never shown or clickable.
  // The shell opens without fetching: a sync here would race the page's own code write.
  // layout/theme.liquid and this snippet both load this file, so two copies run; the first
  // wraps fetch, the second owns window.CartDrawer. They share one pending state.
  const pend = window.__cdPending || (window.__cdPending = { on: false, addDoneAt: 0, synced: false, timer: null, cap: null });

  function isAddRequest(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = ((init && init.method) || (input && typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    return method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(String(url).replace(location.origin, ''));
  }

  function showDrawerShell() {
    const drawer = qs(SELECTORS.drawer);
    const overlay = qs(SELECTORS.overlay);
    if (!drawer || !overlay) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function beginPending() {
    const drawer = qs(SELECTORS.drawer);
    // already open: the drawer's own Add buttons show their own state. Another popup holding
    // the page (editorial-v2 "buy with" popup locks body scroll) keeps its own flow, as before.
    if (!drawer || drawer.classList.contains('is-open')) return;
    if (document.body.style.overflow === 'hidden') return;
    pend.on = true; pend.synced = false; pend.addDoneAt = 0;
    drawer.classList.add('is-adding', 'is-pricing');
    drawer.setAttribute('aria-busy', 'true');
    showDrawerShell();
    clearTimeout(pend.cap);
    // last resort: never reveal a stale total, fetch first, then unmask
    pend.cap = setTimeout(() => {
      if (pend.on) getCart().then(render).catch(() => {}).finally(endPending);
    }, 15000);
  }

  function endPending() {
    const drawer = qs(SELECTORS.drawer);
    if (drawer) {
      drawer.classList.remove('is-adding', 'is-pricing');
      drawer.setAttribute('aria-busy', 'false');
    }
    clearTimeout(pend.timer); clearTimeout(pend.cap);
    pend.on = false; pend.synced = false; pend.addDoneAt = 0;
  }

  function addSettled(ok) {
    if (!pend.on) return;
    if (!ok) {
      // nothing was added: put the page back as it was (the page shows its own error)
      if (!pend.addDoneAt) { endPending(); closeDrawer(); }
      return;
    }
    pend.addDoneAt = performance.now();
    // No early /cart.js here: Shopify queues cart requests per cart, so an extra read made
    // the Sasha page's EXTRA10 write land ~0.2 s later. The page's own refresh renders it.
    // a page that never calls CartDrawer.open()/refresh() still gets a final total. 8 s, past
    // the 6 s cap every product page gives its own code write (ASPEN2, POPPY2, SHAWLBOGO...),
    // so this can never unmask a total before the page's code has had its chance to land.
    clearTimeout(pend.timer);
    pend.timer = setTimeout(() => {
      if (pend.on) getCart().then(render).catch(() => {}).finally(endPending);
    }, 8000);
  }

  // one wrapper per page: the flag lives on window because other scripts (Sentry) wrap fetch
  // on top of ours and hide any property set on the function
  if (window.fetch && !window.__cdFetchWrapped) {
    window.__cdFetchWrapped = true;
    const nativeFetch = window.fetch;
    const wrapped = function (input, init) {
      let isAdd = false;
      try { isAdd = isAddRequest(input, init); if (isAdd) beginPending(); } catch (e) { isAdd = false; }
      const p = nativeFetch.apply(window, arguments);
      if (isAdd && pend.on) p.then((r) => addSettled(r.ok), () => addSettled(false));
      return p;
    };
    window.fetch = wrapped;
  }

  // ---- Socks Buy 10 Get 10: the order code follows the cart, not the button ----
  // The socks offer page attaches BUY10GET10 only from its Buy 10 Get 10 button. Shoppers
  // also reach 20 pairs by adding Buy 5 Get 5 twice or pressing + in this drawer, and paid
  // $79.98 instead of $69.99 (#20269, #20280, 2026-09-26). Every cart fetch now attaches the
  // code at 4+ units of the 5-pair set and takes it off below that, keeping any other code.
  // A v1 cart (two 10-packs + the code, 9-24 to 9-25) keeps its code but never gains it, so
  // the control arm's 10-pack carts are never discounted.
  const BUNDLE = { code: 'BUY10GET10', variantId: 52451308962080, minQty: 4, v1VariantId: 52336717660448, v1MinQty: 2 };
  let bundleSync = null;
  let bundleGaveUp = false;

  function unitsOf(variantId) {
    return cart.items.filter((i) => i.variant_id === variantId).reduce((n, i) => n + i.quantity, 0);
  }

  async function syncBundleCode() {
    // open() and refresh() fetch at the same moment; the second waits for the first's update
    if (bundleSync) { await bundleSync; return; }
    if (bundleGaveUp || !cart || !cart.items) return;
    const codes = codesOnCart(cart);
    const has = codes.some((c) => c.toUpperCase() === BUNDLE.code);
    const want = unitsOf(BUNDLE.variantId) >= BUNDLE.minQty;
    const keep = want || unitsOf(BUNDLE.v1VariantId) >= BUNDLE.v1MinQty;
    if (has ? keep : !want) return;
    const next = want ? codes.concat(BUNDLE.code) : codes.filter((c) => c.toUpperCase() !== BUNDLE.code);
    bundleSync = (async () => {
      try {
        await fetchJSON('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: next.join(',') }),
        });
        cart = await fetchJSON('/cart.js');
        // code switched off in admin: stop retrying on every fetch for this page
        const nowHas = codesOnCart(cart).some((c) => c.toUpperCase() === BUNDLE.code);
        if (want && !nowHas) bundleGaveUp = true;
      } catch (err) {
        console.error('Bundle code sync failed:', err);
      }
    })();
    // a hung request must not leave the drawer blank (same 6s cap as the socks page)
    try {
      await Promise.race([bundleSync, new Promise((res) => setTimeout(res, 6000))]);
    } finally {
      bundleSync = null;
    }
  }

  // ---- Sasha two-pack: EXTRA10 follows the cart, not the button ----
  // The Sasha page attaches EXTRA10 only from its tier-2 button. Shoppers also reach two
  // blazers by adding one and pressing + in this drawer, or by adding the single tier
  // twice, and were charged $99.98 against a page advertising $89.98. This is the same
  // leak as the socks Buy 10 Get 10 above (#20269, #20280). Every cart fetch now attaches
  // the code at 2+ blazer units and takes it off below that, keeping any other code.
  // Shopify allows one order-level code per order, so a cart already carrying
  // BUY10GET10 is left alone rather than fighting it.
  // 2026-10-11: a third tier. Three or more blazers carry EXTRA25 ($25 off, three for
  // $124.97) instead of EXTRA10. Codes are listed best first; the cart gets the first one
  // whose blazer count it meets and loses the others, since only one order code applies.
  const SASHA = { productId: 10072203854112, tiers: [{ code: 'EXTRA25', minQty: 3 }, { code: 'EXTRA10', minQty: 2 }] };
  let sashaSync = null;
  const sashaDead = {};

  function sashaUnits() {
    return cart.items
      .filter((i) => i.product_id === SASHA.productId)
      .reduce((n, i) => n + i.quantity, 0);
  }

  async function syncSashaCode() {
    if (sashaSync) { await sashaSync; return; }
    if (!cart || !cart.items) return;
    const codes = codesOnCart(cart);
    const ours = SASHA.tiers.map((t) => t.code);
    const isOurs = (c) => ours.includes(c.toUpperCase());
    const hasBundle = codes.some((c) => c.toUpperCase() === BUNDLE.code);
    const units = sashaUnits();
    const target = hasBundle ? null : SASHA.tiers.find((t) => units >= t.minQty && !sashaDead[t.code]);
    const want = target ? target.code : null;
    const current = codes.filter(isOurs).map((c) => c.toUpperCase());
    if (want ? (current.length === 1 && current[0] === want) : current.length === 0) return;
    const next = codes.filter((c) => !isOurs(c)).concat(want ? [want] : []);
    sashaSync = (async () => {
      try {
        await fetchJSON('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: next.join(',') }),
        });
        cart = await fetchJSON('/cart.js');
        // code switched off in admin: stop retrying it on every fetch for this page. A dead
        // EXTRA25 falls back to EXTRA10 on the next fetch rather than leaving no code.
        const nowHas = codesOnCart(cart).some((c) => c.toUpperCase() === want);
        if (want && !nowHas) {
          sashaDead[want] = true;
          // the write above already took the other Sasha code off, so put the fallback on
          // now: a shopper who checks out straight from this drawer must not carry no code
          const fb = SASHA.tiers.find((t) => units >= t.minQty && !sashaDead[t.code]);
          if (fb) {
            await fetchJSON('/cart/update.js', {
              method: 'POST',
              body: JSON.stringify({ discount: codesOnCart(cart).filter((c) => !isOurs(c)).concat(fb.code).join(',') }),
            });
            cart = await fetchJSON('/cart.js');
          }
        }
      } catch (err) {
        console.error('Sasha code sync failed:', err);
      }
    })();
    // a hung request must not leave the drawer blank (same 6s cap as the bundle sync)
    try {
      await Promise.race([sashaSync, new Promise((res) => setTimeout(res, 6000))]);
    } finally {
      sashaSync = null;
    }
  }

  async function addToCart(variantId, qty = 1, properties = {}) {
    const data = { items: [{ id: variantId, quantity: qty, properties }] };
    const res = await fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await getCart();
    return res;
  }

  async function changeLineItem(lineKey, qty) {
    await fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    });
    await getCart();
  }

  // ---- Drawer Open/Close ----

  function openDrawer() {
    const drawer = qs(SELECTORS.drawer);
    const overlay = qs(SELECTORS.overlay);
    if (!drawer || !overlay) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    getCart().then(render);
  }

  function closeDrawer() {
    const drawer = qs(SELECTORS.drawer);
    const overlay = qs(SELECTORS.overlay);
    if (!drawer || !overlay) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    closeVariantPopup();
  }

  // ---- Free pairs (Willow, Fakrul 2026-10-23) ----
  // Products sold "Buy 1 Get 1 Free" by quantity (automatic discounts on the product) show their free pairs as FREE:
  // half the pairs, rounded down. The paid pairs share what Shopify actually charges, so the lines still add up to
  // the cart total, and struck prices use the regular price (cents, shop currency: { regular, price }); other
  // currencies scale by the same ratio. Checkout still shows Shopify's own split by discount name.
  const FREE_PAIRS = { 'the-willow-shapewear-fleece-tights': { regular: 4999, price: 3499 } };
  let freePlan = {};

  function freePairsPlan(items) {
    const plan = {};
    Object.keys(FREE_PAIRS).forEach(function (handle) {
      const cfg = FREE_PAIRS[handle];
      const lines = items.filter(function (it) { return it.handle === handle && it.price > 0; });
      if (!lines.length) return;
      const units = lines.reduce(function (n, it) { return n + it.quantity; }, 0);
      const charged = lines.reduce(function (n, it) { return n + it.final_line_price; }, 0);
      const listed = lines.reduce(function (n, it) { return n + it.original_line_price; }, 0);
      const free = {};
      let left = charged < listed ? Math.floor(units / 2) : 0;   // only when the discount really applied
      lines.forEach(function (it) { const f = Math.min(Math.floor(it.quantity / 2), left); free[it.key] = f; left -= f; });
      for (let i = lines.length - 1; i >= 0 && left > 0; i--) {   // odd pairs left over: the last lines, so a 1-pair line reads FREE
        if (free[lines[i].key] < lines[i].quantity) { free[lines[i].key] += 1; left -= 1; }
      }
      const paidUnits = lines.reduce(function (n, it) { return n + it.quantity - free[it.key]; }, 0);
      let given = 0;
      lines.forEach(function (it) {
        const paid = it.quantity - free[it.key];
        const shown = paidUnits ? Math.floor(charged * paid / paidUnits) : 0;
        given += shown;
        plan[it.key] = { free: free[it.key], paid: paid, shown: shown,
                         regular: Math.round(it.original_price * cfg.regular / cfg.price) * it.quantity };
      });
      const first = lines.find(function (it) { return plan[it.key].paid > 0; });
      if (first) plan[first.key].shown += charged - given;          // rounding cents, so the lines add up exactly
    });
    return plan;
  }

  // ---- Rendering ----

  function render() {
    if (!cart) return;

    const itemsEl = qs(SELECTORS.items);
    const emptyEl = qs(SELECTORS.empty);
    const checkoutEl = qs(SELECTORS.checkout);
    const headerCount = qs(SELECTORS.headerCount);

    const hasItems = cart.item_count > 0;

    if (emptyEl) emptyEl.style.display = hasItems ? 'none' : 'flex';
    if (itemsEl) itemsEl.style.display = hasItems ? 'block' : 'none';
    if (checkoutEl) checkoutEl.style.display = hasItems ? 'flex' : 'none';

    freePlan = freePairsPlan(cart.items || []);
    if (itemsEl) {
      itemsEl.innerHTML = '';
      cart.items.forEach((item) => {
        if (item.properties && item.properties._is_free_gift) return;
        itemsEl.appendChild(buildLineItem(item));
      });
    }

    if (headerCount) headerCount.textContent = cart.item_count;

    renderShippingBar();
    renderOrderDiscounts();
    renderTotalLine();
    updateUpsellButtons();
    updateCartCount();

    if (pend.on && pend.synced) endPending();
  }

  function buildLineItem(item) {
    const template = qs(SELECTORS.itemTemplate);
    if (!template) return document.createElement('div');

    const clone = template.content.firstElementChild.cloneNode(true);
    clone.dataset.lineKey = item.key;

    const img = clone.querySelector('.cd-item__img img');
    if (img && item.image) {
      img.src = getSizedImageUrl(item.image, '200x');
      img.alt = item.title;
    }

    // Product name as link
    const nameEl = clone.querySelector('.cd-item__name');
    nameEl.textContent = item.product_title;
    if (item.url) nameEl.href = item.url;

    const variantEl = clone.querySelector('.cd-item__variant');
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantEl.textContent = item.variant_title;
    } else {
      variantEl.style.display = 'none';
    }

    // Visible line properties (e.g. the free blanket on the B1G1 offer)
    if (item.properties) {
      Object.keys(item.properties).forEach(function (k) {
        if (k.charAt(0) === '_' || !item.properties[k]) return;
        const prop = document.createElement('div');
        prop.className = 'cd-item__variant cd-item__prop';
        prop.style.cssText = 'color:#7A3B2E;font-weight:600;';
        prop.textContent = k + ': ' + item.properties[k];
        variantEl.parentNode.insertBefore(prop, variantEl.nextSibling);
      });
    }

    // Line price
    const linePriceEl = clone.querySelector('.cd-item__line-price');
    if (linePriceEl) linePriceEl.textContent = formatMoney(item.final_line_price);

    // Free gift lines ($0): price shows FREE, quantity is locked
    if (item.price === 0) {
      if (linePriceEl) {
        linePriceEl.textContent = 'FREE';
        linePriceEl.style.cssText = 'color:#7A3B2E;font-weight:700;letter-spacing:.06em;';
      }
      const qtyEl = clone.querySelector('.cd-item__qty');
      if (qtyEl) qtyEl.style.display = 'none';
    }

    // A paid item a discount made fully free (e.g. Buy 5 Get 5 Free): FREE, not $0.00.
    // Quantity stays editable, unlike a $0 gift, because the discount follows the quantity.
    const discountFree = item.price > 0 && item.final_line_price === 0 && item.original_line_price > 0;
    if (discountFree && linePriceEl) {
      linePriceEl.textContent = 'FREE';
      linePriceEl.style.cssText = 'color:#7A3B2E;font-weight:700;letter-spacing:.06em;';
    }

    // Compare price (show if discounted)
    const comparePriceEl = clone.querySelector('.cd-item__compare-price');
    if (comparePriceEl && item.original_line_price > item.final_line_price) {
      comparePriceEl.textContent = formatMoney(item.original_line_price);
      comparePriceEl.style.display = '';
    }

    // Promo badge
    const promoBadge = clone.querySelector('.cd-item__promo-badge');
    if (promoBadge && item.original_line_price > item.final_line_price) {
      const savings = item.original_line_price - item.final_line_price;
      const pct = Math.round((savings / item.original_line_price) * 100);
      promoBadge.textContent = `${pct}% OFF (-${formatMoney(savings)})`;
      // a fully free line reads the discount's own name ("Buy 5 Get 5 Free"), not "100% OFF"
      const alloc = (item.line_level_discount_allocations || [])[0];
      const allocTitle = alloc && alloc.discount_application && alloc.discount_application.title;
      if (discountFree && allocTitle) promoBadge.textContent = allocTitle;
      promoBadge.style.display = '';
    }

    // Free pairs (Willow): FREE pairs, paid pairs at their share of the real charge, struck from the regular price
    const fp = freePlan[item.key];
    if (fp) {
      const freeStyle = 'color:#7A3B2E;font-weight:700;letter-spacing:.06em;';
      if (linePriceEl) {
        if (fp.paid === 0) { linePriceEl.textContent = 'FREE'; linePriceEl.style.cssText = freeStyle; }
        else { linePriceEl.textContent = formatMoney(fp.shown); linePriceEl.style.cssText = ''; }
      }
      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(fp.regular);
        comparePriceEl.style.display = fp.regular > fp.shown ? '' : 'none';
      }
      if (promoBadge) {
        if (fp.free > 0) {
          promoBadge.textContent = fp.paid === 0 ? (fp.free > 1 ? fp.free + ' pairs FREE' : 'FREE pair') : fp.free + ' FREE';
          promoBadge.style.display = '';
        } else {
          promoBadge.style.display = 'none';
        }
      }
    }

    // Quantity
    const qtyInput = clone.querySelector('.cd-item__qty-input');
    qtyInput.value = item.quantity;

    clone.querySelector('.cd-item__qty-minus').addEventListener('click', () => {
      if (isUpdating) return;
      handleQuantityChange(item.key, Math.max(0, item.quantity - 1), clone);
    });

    clone.querySelector('.cd-item__qty-plus').addEventListener('click', () => {
      if (isUpdating) return;
      handleQuantityChange(item.key, item.quantity + 1, clone);
    });

    qtyInput.addEventListener('change', () => {
      if (isUpdating) return;
      const val = parseInt(qtyInput.value, 10);
      if (val >= 0) handleQuantityChange(item.key, val, clone);
    });

    clone.querySelector('.cd-item__remove').addEventListener('click', () => {
      if (isUpdating) return;
      clone.classList.add('is-removing');
      setTimeout(() => handleQuantityChange(item.key, 0), 250);
    });

    return clone;
  }

  async function handleQuantityChange(key, qty) {
    if (isUpdating) return;
    isUpdating = true;
    try {
      await changeLineItem(key, qty);
      render();
    } catch (err) {
      console.error('Cart update failed:', err);
    } finally {
      isUpdating = false;
    }
  }

  // ---- Free Shipping Bar ----

  function renderShippingBar() {
    const bar = qs('#cd-shipping-bar');
    const textEl = qs('#cd-shipping-text');
    const progressEl = qs('#cd-shipping-progress');
    if (!bar || !textEl || !progressEl || !cart) return;

    const threshold = CFG.freeShippingThreshold || 0;
    if (threshold <= 0) { bar.style.display = 'none'; return; }

    const total = cart.total_price;
    const remaining = Math.max(0, threshold - total);
    const pct = Math.min(100, (total / threshold) * 100);

    if (remaining <= 0) {
      textEl.innerHTML = '<span class="cd-shipping-bar__check-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg></span> <strong>You\'ve unlocked Free Shipping!</strong>';
      bar.classList.add('is-complete');
    } else {
      textEl.innerHTML = `Spend <strong>${formatMoney(remaining)}</strong> more for <strong>Free Shipping</strong>`;
      bar.classList.remove('is-complete');
    }

    progressEl.style.width = `${pct}%`;
  }

  // ---- Order-level discounts (codes) as their own lines above Total ----
  // Line discounts already show on each item; an order code like BUY10GET10 had no line
  // of its own and only disappeared into the Total.
  const ORDER_DISCOUNT_LABELS = { BUY10GET10: 'Buy 10 Get 10 bonus' };

  function renderOrderDiscounts() {
    const totalLine = qs('.cd-total-line');
    if (!totalLine || !cart) return;
    let box = qs('#cd-order-discounts');
    if (!box) {
      box = document.createElement('div');
      box.id = 'cd-order-discounts';
      box.style.cssText = 'margin-bottom:8px;';
      totalLine.parentNode.insertBefore(box, totalLine);
    }
    const apps = (cart.cart_level_discount_applications || []).filter((a) => a.total_allocated_amount > 0);
    box.innerHTML = '';
    box.style.display = apps.length ? '' : 'none';
    apps.forEach((a) => {
      const key = String(a.title || '').toUpperCase();
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;align-items:center;font-size:12px;color:#7A3B2E;font-weight:600;line-height:1.5;';
      const label = document.createElement('span');
      label.textContent = ORDER_DISCOUNT_LABELS[key] || a.title;
      const amount = document.createElement('span');
      amount.textContent = '-' + formatMoney(a.total_allocated_amount);
      row.appendChild(label);
      row.appendChild(amount);
      box.appendChild(row);
    });
  }

  // ---- Total Line with Savings ----

  function renderTotalLine() {
    const subtotalEl = qs(SELECTORS.subtotal);
    const compareEl = qs(SELECTORS.totalCompare);
    const savingsEl = qs(SELECTORS.totalSavings);
    if (!cart) return;

    // Format with currency code
    const currencyCode = cart.currency || '';
    if (subtotalEl) {
      subtotalEl.textContent = `${formatMoney(cart.total_price)} ${currencyCode}`;
    }

    // Calculate savings
    let originalTotal = cart.original_total_price || cart.total_price;
    // Free pairs (Willow): the total is struck from the regular price too, like its lines
    (cart.items || []).forEach(function (it) {
      if (freePlan[it.key]) originalTotal += freePlan[it.key].regular - it.original_line_price;
    });
    const savings = originalTotal - cart.total_price;

    if (compareEl) {
      if (savings > 0) {
        compareEl.textContent = formatMoney(originalTotal);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // The savings row only shows when it has something to say (compact drawer 2026-10-17)
    const sumRow = qs('#cd-sum');
    if (sumRow) {
      const codesBox = qs('#cd-order-discounts');
      const hasCodes = !!(codesBox && codesBox.style.display !== 'none' && codesBox.children.length);
      sumRow.style.display = savings > 0 || hasCodes ? '' : 'none';
    }

    if (savingsEl) {
      if (savings > 0) {
        savingsEl.textContent = `You save ${formatMoney(savings)}`;
        savingsEl.style.display = '';
      } else {
        savingsEl.style.display = 'none';
      }
    }
  }

  // ---- Promo Code ----
  // A typed code is MERGED onto the codes already on the cart, then verified against the
  // cart before anything is shown to the shopper.
  //
  // The previous build wrote the code to a cart ATTRIBUTE (so it never applied), always
  // reported "Applied!", and rewrote the checkout link to carry the code as a query
  // parameter. That parameter REPLACES the codes already on the cart, so a Sasha cart
  // displaying $89.98 with EXTRA10 was charged $99.98 at checkout (orders #21043, #20185).
  // Never rewrite the checkout link again: the cart's own codes carry into checkout.

  function codesOnCart(c) {
    return ((c && c.discount_codes) || [])
      .filter(function (d) { return d.applicable !== false; })
      .map(function (d) { return d.code; });
  }

  // What ONE code actually took off, so the note agrees with the figure
  // renderOrderDiscounts() prints a few pixels below it. Do not use
  // original_total_price - total_price: that is the whole cart's saving, including the
  // two-pack's own line discount, and it reported EXTRA10 as $39.99 when it allocates $10.
  // An order code is listed in cart_level_discount_applications; a line-level code is not,
  // so fall back to its line allocations rather than summing both and double counting.
  function codeSavings(c, code) {
    const up = String(code || '').toUpperCase();
    const atOrder = ((c && c.cart_level_discount_applications) || [])
      .filter(function (a) { return String(a.title || '').toUpperCase() === up; })
      .reduce(function (n, a) { return n + (a.total_allocated_amount || 0); }, 0);
    if (atOrder > 0) return atOrder;
    return ((c && c.items) || []).reduce(function (n, it) {
      return n + ((it.line_level_discount_allocations || [])
        .filter(function (a) {
          return String(((a.discount_application || {}).title) || '').toUpperCase() === up;
        })
        .reduce(function (m, a) { return m + (a.amount || 0); }, 0));
    }, 0);
  }

  let promoDefaultNote = '';
  let promoBusy = false;

  function setPromoNote(msg, ok) {
    const note = qs('.cd-promo-code__note');
    if (!note) return;
    note.textContent = msg;
    note.style.color = ok === true ? '#1BB76E' : ok === false ? '#c23a3a' : '';
    note.style.fontWeight = ok == null ? '' : '600';
    // the default note ships at 11px; these are now the drawer's only failure messages
    note.style.fontSize = ok == null ? '' : '13px';
  }

  // POST a code list and hand back the cart as it actually ended up.
  async function putCodes(list) {
    await fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ discount: list.join(',') }),
    });
    return fetchJSON('/cart.js');
  }

  function initPromoCode() {
    const applyBtn = qs(SELECTORS.promoApply);
    const input = qs(SELECTORS.promoInput);
    if (!applyBtn || !input) return;

    const noteEl = qs('.cd-promo-code__note');
    promoDefaultNote = (noteEl && noteEl.textContent.trim()) || 'One code per order.';

    applyBtn.addEventListener('click', async () => {
      // one burst of clicks or held Enter must not fire several cart writes
      if (promoBusy) return;
      const code = input.value.trim();
      if (!code) { setPromoNote(promoDefaultNote, null); return; }

      promoBusy = true;
      applyBtn.textContent = 'Applying...';
      applyBtn.disabled = true;
      setPromoNote('', null);

      try {
        const before = await fetchJSON('/cart.js');
        const prior = codesOnCart(before);
        const dupe = prior.filter(function (k) { return k.toUpperCase() === code.toUpperCase(); })[0];

        if (dupe) {
          setPromoNote(dupe + ' is already applied to this order.', true);
          input.value = '';
        } else {
          const after = await putCodes(prior.concat(code));
          const entry = (after.discount_codes || []).filter(function (d) {
            return d.code.toUpperCase() === code.toUpperCase();
          })[0];

          if (entry && entry.applicable !== false) {
            // It stuck. Adopt this cart and repaint every total so the shopper sees it.
            cart = after;
            render();
            const saved = codeSavings(after, entry.code);
            setPromoNote(
              saved > 0 ? entry.code + ' applied. You save ' + formatMoney(saved) + '.'
                        : entry.code + ' applied.',
              true
            );
            input.value = '';
          } else {
            // Put the cart back exactly as it was, then explain. Shopify takes one
            // order-level code per order unless the codes are set combinable, so on a cart
            // already carrying EXTRA10 or BUY10GET10 a second code cannot apply.
            const restored = await putCodes(prior);
            cart = restored;
            render();
            if (prior.length) {
              const kept = codeSavings(restored, prior[0]);
              setPromoNote(
                kept > 0
                  ? prior[0] + ' is already on this order and saves you ' + formatMoney(kept) +
                    '. Only one code can be used per order.'
                  : prior[0] + ' is already on this order. Only one code can be used per order.',
                false
              );
            } else {
              setPromoNote('"' + code + '" is not a valid code.', false);
            }
          }
        }
      } catch (err) {
        console.error('Promo code failed:', err);
        setPromoNote('We could not check that code. Please try again.', false);
        try { cart = await fetchJSON('/cart.js'); render(); } catch (e) { /* keep the drawer up */ }
      } finally {
        promoBusy = false;
        applyBtn.textContent = 'Apply';
        applyBtn.disabled = false;
      }
    });

    // typing again must not leave the previous code's verdict on screen
    input.addEventListener('input', () => {
      if (!promoBusy) setPromoNote(promoDefaultNote, null);
    });

    // Submit on Enter
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyBtn.click();
      }
    });
  }

  // ---- Upsells ----

  function updateUpsellButtons() {
    if (!cart) return;
    const cartVariantIds = cart.items.map(i => i.variant_id);
    const cartProductIds = cart.items.map(i => i.product_id);
    const cartHandles = cart.items.map(i => i.handle);

    // Hide add-ons whose product is already in the cart (any variant), or another listing of
    // the same item (data-same-item, e.g. the shawl's Buy 1 Get 1 listing). 2026-10-17
    let visible = 0;
    qsa('.cd-upsell-card').forEach(card => {
      const pid = parseInt(card.dataset.productId, 10);
      const same = [card.dataset.upsellHandle].concat((card.dataset.sameItem || '').split('+')).filter(Boolean);
      const hide = card.dataset.failed === 'true' || (pid && cartProductIds.includes(pid)) ||
        same.some(h => cartHandles.includes(h));
      card.style.display = hide ? 'none' : '';
      if (!hide) visible++;
    });
    const section = qs(SELECTORS.upsells);
    if (section) section.style.display = visible ? '' : 'none';

    qsa('.cd-upsell-card__add').forEach(btn => {
      const vid = parseInt(btn.dataset.variantId, 10);
      if (cartVariantIds.includes(vid)) {
        btn.textContent = 'Added';
        btn.classList.add('is-added');
      } else if (btn.classList.contains('is-added')) {
        btn.textContent = '+ Add';
        btn.classList.remove('is-added');
      }
    });
  }

  function fetchHandleUpsells() {
    qsa('[data-upsell-handle]').forEach(card => {
      const handle = card.dataset.upsellHandle;
      if (!handle) return;

      fetch(`/products/${handle}.js`)
        .then(r => r.json())
        .then(prod => {
          card.classList.remove('cd-upsell-card--loading');
          const v = prod.variants[0];
          card.dataset.variantId = v.id;
          card.dataset.productHandle = prod.handle;
          card.dataset.productId = prod.id;
          card.dataset.optionNames = JSON.stringify(prod.options || []);
          card.dataset.hasVariants = prod.variants.length > 1 ? 'true' : 'false';
          card.dataset.variants = JSON.stringify(prod.variants);

          // Image
          const imgEl = card.querySelector('.cd-upsell-card__img');
          if (imgEl && prod.featured_image) {
            imgEl.innerHTML = `<img src="${prod.featured_image}" alt="${prod.title.replace(/"/g, '&quot;')}" loading="lazy" width="60" height="60">`;
          }

          // Title & prices
          card.querySelector('.cd-upsell-card__name').textContent = prod.title;
          const priceEl = card.querySelector('.cd-upsell-card__price');
          if (priceEl) priceEl.textContent = formatMoney(v.price);

          // Compare price
          if (v.compare_at_price && v.compare_at_price > v.price) {
            const compareEl = card.querySelector('.cd-upsell-card__compare');
            if (compareEl) {
              compareEl.textContent = formatMoney(v.compare_at_price);
              compareEl.style.display = '';
            } else {
              const pricesEl = card.querySelector('.cd-upsell-card__prices');
              if (pricesEl) {
                const newCompare = document.createElement('span');
                newCompare.className = 'cd-upsell-card__compare';
                newCompare.textContent = formatMoney(v.compare_at_price);
                if (priceEl) priceEl.after(newCompare); else pricesEl.appendChild(newCompare);
              }
            }
            // % badge from the same compare-at price (compact drawer 2026-10-17)
            const pctEl = card.querySelector('.cd-upsell-card__pct');
            const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
            if (pctEl && pct >= 1) {
              pctEl.textContent = `${pct}% OFF`;
              pctEl.style.display = '';
            }
          }

          // Add button
          const addBtn = card.querySelector('.cd-upsell-card__add');
          addBtn.dataset.variantId = v.id;
          addBtn.disabled = false;

          // Render review stars from data attributes
          const starCount = parseInt(card.dataset.stars) || 0;
          const reviewCount = parseInt(card.dataset.reviews) || 0;
          if (starCount > 0) {
            const ratingEl = card.querySelector('.cd-upsell-card__rating');
            if (ratingEl) renderStars(ratingEl, starCount, reviewCount);
          }

          updateUpsellButtons();
        })
        .catch(() => { card.dataset.failed = 'true'; card.style.display = 'none'; updateUpsellButtons(); });
    });
  }

  function renderStars(container, rating, count) {
    let html = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(rating)) {
        html += '<span class="cd-star cd-star--filled">&#9733;</span>';
      } else if (i - 0.5 <= rating) {
        html += '<span class="cd-star cd-star--filled">&#9733;</span>';
      } else {
        html += '<span class="cd-star">&#9733;</span>';
      }
    }
    if (count > 0) {
      html += `<span class="cd-upsell-card__reviews">${count} reviews</span>`;
    }
    container.innerHTML = html;
  }

  function initUpsellNav() {
    const track = qs(SELECTORS.upsellGrid);
    const prev = qs(SELECTORS.upsellPrev);
    const next = qs(SELECTORS.upsellNext);
    if (!track || !prev || !next) return;

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -290, behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      track.scrollBy({ left: 290, behavior: 'smooth' });
    });
  }

  function handleUpsellAdd(e) {
    const btn = e.target.closest('.cd-upsell-card__add');
    if (!btn || btn.classList.contains('is-added') || btn.disabled || isUpdating) return;

    const card = btn.closest('.cd-upsell-card');
    if (!card) return;

    // Multi-variant — open popup
    if (card.dataset.hasVariants === 'true') {
      openVariantPopup(card);
      return;
    }

    // Single variant — add directly
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    isUpdating = true;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    addToCart(variantId, 1)
      .then(() => {
        btn.textContent = 'Added';
        btn.classList.add('is-added');
        render();
      })
      .catch(err => {
        console.error('Upsell add failed:', err);
        btn.textContent = '+ Add';
      })
      .finally(() => { btn.disabled = false; isUpdating = false; });
  }

  // ---- Mini Variant Popup ----

  function openVariantPopup(card) {
    const popup = qs(SELECTORS.variantPopup);
    if (!popup) return;

    let variants;
    try { variants = JSON.parse(card.dataset.variants); }
    catch { return; }
    // Sold-out variants are not offered in the popup (picking one made Add to Cart do nothing)
    variants = variants.filter(v => v.available !== false);
    if (!variants.length) return;

    const imgEl = card.querySelector('.cd-upsell-card__img img');
    popupProduct = {
      title: card.querySelector('.cd-upsell-card__name').textContent,
      image: imgEl ? imgEl.src : '',
      variants: variants,
      options: [],
    };

    if (variants.length > 0 && variants[0].options) {
      const optionCount = variants[0].options.length;
      for (let i = 0; i < optionCount; i++) {
        const values = [...new Set(variants.map(v => v.options[i]))];
        let names = [];
        try { names = JSON.parse(card.dataset.optionNames || '[]'); } catch (e) {}
        let guessedName = (typeof names[i] === 'string' && names[i]) || (names[i] && names[i].name) || `Option ${i + 1}`;
        if (/^Option \d+$/.test(guessedName) && values.some(v => /^(XS|S|M|L|XL|XXL|\d+)$/i.test(v))) guessedName = 'Size';
        else if (/^Option \d+$/.test(guessedName) && values.some(v => /^(red|blue|green|black|white|gold|pink|wine|sky blue|navy|cream)/i.test(v))) guessedName = 'Color';
        popupProduct.options.push({ name: guessedName, values });
      }
    }

    popupSelectedOptions = variants[0].options.slice();

    qs(SELECTORS.popupImg).src = popupProduct.image;
    qs(SELECTORS.popupName).textContent = popupProduct.title;
    qs(SELECTORS.popupPrice).textContent = formatMoney(variants[0].price);

    const optionsEl = qs(SELECTORS.popupOptions);
    optionsEl.innerHTML = '';

    popupProduct.options.forEach((opt, optIdx) => {
      const group = document.createElement('div');
      group.className = 'cd-variant-popup__option-group';
      group.innerHTML = `<span class="cd-variant-popup__option-label">${opt.name}</span>`;
      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'cd-variant-popup__option-values';

      opt.values.forEach(val => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cd-variant-popup__option-btn';
        btn.textContent = val;
        btn.dataset.optionIndex = optIdx;
        btn.dataset.value = val;
        if (val === popupSelectedOptions[optIdx]) btn.classList.add('active');
        btn.addEventListener('click', () => selectPopupOption(optIdx, val));
        valuesWrap.appendChild(btn);
      });

      group.appendChild(valuesWrap);
      optionsEl.appendChild(group);
    });

    popup.style.display = 'block';
  }

  function selectPopupOption(optIdx, value) {
    popupSelectedOptions[optIdx] = value;

    qsa(`.cd-variant-popup__option-btn[data-option-index="${optIdx}"]`).forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === value);
    });

    const match = popupProduct.variants.find(v =>
      v.options.every((o, i) => o === popupSelectedOptions[i])
    );

    if (match) {
      qs(SELECTORS.popupPrice).textContent = formatMoney(match.price);
      if (match.featured_image && match.featured_image.src) {
        qs(SELECTORS.popupImg).src = match.featured_image.src;
      }
    }
  }

  function handlePopupAtc() {
    if (!popupProduct || !popupProduct.variants) return;

    const match = popupProduct.variants.find(v =>
      v.options.every((o, i) => o === popupSelectedOptions[i])
    );

    if (!match) return;
    if (match.available === false) return;

    const btn = qs(SELECTORS.popupAtc);
    if (!btn || btn.disabled) return;

    btn.textContent = 'Adding...';
    btn.disabled = true;
    isUpdating = true;

    addToCart(match.id, 1)
      .then(() => {
        closeVariantPopup();
        render();
      })
      .catch(err => {
        console.error('Popup ATC failed:', err);
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
      })
      .finally(() => { isUpdating = false; });
  }

  function closeVariantPopup() {
    const popup = qs(SELECTORS.variantPopup);
    if (popup) popup.style.display = 'none';
    popupProduct = null;
    popupSelectedOptions = [];
    isUpdating = false;
    const btn = qs(SELECTORS.popupAtc);
    if (btn) { btn.textContent = 'Add to Cart'; btn.disabled = false; }
  }

  // ---- Cart Count (header badge) ----

  function updateCartCount() {
    if (!cart) return;
    const count = cart.item_count;
    // Header badge. Only touch .cart-count-bubble: the icon SVG also sits in a span inside
    // #cart-icon-bubble, and writing the count into every span there wiped the icon.
    const link = document.getElementById('cart-icon-bubble');
    if (link) {
      let bubble = link.querySelector('.cart-count-bubble');
      if (count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          link.appendChild(bubble);
        }
        const num = bubble.querySelector('[aria-hidden="true"]');
        const label = bubble.querySelector('.visually-hidden');
        if (num) num.textContent = count < 100 ? count : '99+';
        if (label) label.textContent = count === 1 ? '1 item' : count + ' items';
        bubble.style.display = '';
      } else if (bubble) {
        bubble.remove();
      }
    }
    const selectors = ['.cart-count', '[data-cart-count]', '.header__cart-count', '.site-header__cart-count'];
    selectors.forEach(sel => {
      qsa(sel).forEach(el => {
        el.textContent = count;
        const parent = el.closest('[data-cart-count-bubble]');
        if (parent) parent.style.display = count > 0 ? '' : 'none';
      });
    });
  }

  // ---- Image URL Helper ----

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    const match = url.match(/(.*)\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) return `${match[1]}_${size}.${match[2]}${match[3] || ''}`;
    return url;
  }

  // ---- ATC Interception ----

  function interceptAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action="/cart/add"], form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      e.stopPropagation();

      const formData = new FormData(form);
      const id = formData.get('id');
      const qty = parseInt(formData.get('quantity') || '1', 10);
      if (!id) return;

      const submitBtn = form.querySelector('[type="submit"], button:not([type="button"])');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.textContent = 'Adding...'; submitBtn.disabled = true; }

      try {
        await fetchJSON('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: qty }] }),
        });
        await getCart();
        openDrawer();
      } catch (err) {
        console.error('Add to cart failed:', err);
      } finally {
        if (submitBtn) { submitBtn.textContent = originalText; submitBtn.disabled = false; }
      }
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-drawer-add]');
      if (!btn) return;
      const variantId = parseInt(btn.dataset.cartDrawerAdd, 10);
      if (!variantId) return;
      e.preventDefault();
      addToCart(variantId, 1).then(() => openDrawer());
    });
  }

  function interceptCartLinks() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('#cd-checkout-btn, a[href="/checkout"], a[href*="/checkout"]')) return;
      const link = e.target.closest('a[href="/cart"], a[href$="/cart"]');
      if (!link) return;
      e.preventDefault();
      openDrawer();
    });
  }

  function handleCheckout() {
    const btn = qs(SELECTORS.checkout);
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // total still settling (keyboard can reach the link past pointer-events:none)
      if (pend.on) { e.preventDefault(); return; }
      window.location.href = btn.href || '/checkout';
    });
  }

  function handleKeyboard(e) {
    if (e.key === 'Escape') closeDrawer();
  }

  // ---- Init ----

  function init() {
    const closeBtn = qs(SELECTORS.close);
    const overlay = qs(SELECTORS.overlay);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', handleKeyboard);

    // Upsell interactions
    const upsellGrid = qs(SELECTORS.upsellGrid);
    if (upsellGrid) upsellGrid.addEventListener('click', handleUpsellAdd);

    // Variant popup
    const popupAtcBtn = qs(SELECTORS.popupAtc);
    const popupCloseBtn = qs(SELECTORS.popupClose);
    if (popupAtcBtn) popupAtcBtn.addEventListener('click', handlePopupAtc);
    if (popupCloseBtn) popupCloseBtn.addEventListener('click', closeVariantPopup);

    // Upsell carousel nav
    initUpsellNav();

    // Fetch handle-based upsells
    fetchHandleUpsells();

    // Promo code
    initPromoCode();

    interceptAddToCart();
    interceptCartLinks();
    handleCheckout();

    // Listen for cart:refresh events
    document.addEventListener('cart:refresh', () => getCart().then(render));

    getCart().then(render);
  }

  // Expose API
  window.CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: () => getCart().then(render),
    addItem: (variantId, qty) => addToCart(variantId, qty).then(() => { render(); openDrawer(); }),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

